import React, { Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import LoadingSpinner from './components/LoadingSpinner';
import { ErrorBoundary } from 'react-error-boundary';

const HomePage = React.lazy(() => import('./pages/HomePage/HomePage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage/AboutPage'));
const PlayPage = React.lazy(() => import('./pages/PlayPage/PlayPage'));
const NotesPage = React.lazy(() => import('./pages/NotesPage/NotesPage'));
const DetailsPage = React.lazy(() => import('./pages/HomePage/ProjectDetail/ProjectDetail'));
const ContactWindow = React.lazy(() => import('./components/Contact'));
// 错误回调组件
const ErrorFallback: React.FC<{ error: Error }> = ({ error }) => (
  <div role="alert">
    <h2>出错了！</h2>
    <pre>{error.message}</pre>
    <button onClick={() => window.location.reload()}>刷新页面</button>
  </div>
);

// 预加载函数
const prefetchAboutPage = () => {
  const timer = setTimeout(() => {
    import('./pages/AboutPage/AboutPage');
  }, 1000);
  return () => clearTimeout(timer);
};

// 创建一个包装组件来处理页面切换动画
const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20
    },
    in: {
      opacity: 1,
      y: 0
    },
    out: {
      opacity: 0,
      y: -20
    }
  };

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
};

// 路由组件
const AppRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={
          <PageWrapper>
            <HomePage />
          </PageWrapper>
        } />
        <Route path="/work" element={
          <PageWrapper>
            <HomePage />
          </PageWrapper>
        } />
        <Route path="/about" element={
          <PageWrapper>
            <AboutPage />
          </PageWrapper>
        } />
        <Route path="/play" element={
          <PageWrapper>
            <PlayPage />
          </PageWrapper>
        } />
        <Route path="/notes" element={
          <PageWrapper>
            <NotesPage />
          </PageWrapper>}
        />
        <Route path="/work/details/:id" element={
          <PageWrapper>
            <DetailsPage />
          </PageWrapper>}
        />
      </Routes>
    </AnimatePresence>
  );
};

const App: React.FC = () => {
  React.useEffect(() => {
    prefetchAboutPage();
  }, []);

  return (
    <Router>
      <div className="app-container">
        <Suspense fallback={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingSpinner />
          </motion.div>
        }>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <AppRoutes />
          </ErrorBoundary>
        </Suspense>
      </div>
    </Router>
  );
};

export default App;